import $ from 'jquery';
import {
    setFieldValue,
    isFieldRequired,
    getFieldValue,
    ConfigurationError
} from "@ppcp/utils";
import BaseGateway from './BaseGateway';

class ApplePayGateway extends BaseGateway {

    constructor(props) {
        super(props);
        this.capabilities = [...this.capabilities, 'api_form_validation'];
        this._applepay = null;
    }

    get applepay() {
        if (!this._applepay && this.paypal?.Applepay) {
            this._applepay = this.paypal.Applepay();
        }
        return this._applepay;
    }

    /**
     * Check if Apple Pay is available
     * @returns {Promise<boolean>} True if Apple Pay is available and ready
     * @throws {ConfigurationError} When there's a configuration issue (SDK not loaded, currency unsupported)
     */
    async canMakePayment() {
        if (!this.paypal?.Applepay) {
            return false;
        }
        if (!window.ApplePaySession) {
            return false;
        }
        if (!this.settings.supported_currencies.includes(this.controller.getCurrency())) {
            return false;
        }
        if (!ApplePaySession.canMakePayments()) {
            return false;
        }
        // Apple Pay requires payment and cannot be used for orders where total is $0.
        if (!(this.controller.getTotalCents() > 0)) {
            return false;
        }
        try {
            if (!this.applepayConfig) {
                this.applepayConfig = await this.applepay.config();
            }
            return this.applepayConfig.isEligible;
        } catch (error) {
            console.log(error);
            throw new ConfigurationError(error.message);
        }
    }

    /**
     * Check if Apple Pay button should be created
     * @param {HTMLElement|NodeList} container - The container to check
     * @returns {boolean} True if button doesn't already exist
     */
    shouldCreateElement(container) {
        return !$(container).find('button.wc-ppcp_applepay-button').length;
    }

    /**
     * Create Google Pay button in the specified container
     * @param {HTMLElement|NodeList} container - The container element(s) to render button
     * @returns {Object} Object containing button instance or null
     */
    async createPaymentElement(container) {
        const $button = $(this.settings.html.button);
        $button.addClass(`${this.controller.context.getPage()}-context`);
        $button.attr('id', `wc-ppcp_applepay-button-${this.controller.context.getPage()}`);
        $button.css({
            '-webkit-appearance': '-apple-pay-button',
            '-apple-pay-button-type': this.settings.button.type,
            '-apple-pay-button-style': this.settings.button.style,
            'border-radius': this.settings.button.radius,
            'height': this.settings.button.height
        });
        $button.on('click', this.onButtonClick.bind(this));

        this.$button = $button;

        $(container).append($button);
    }

    onButtonClick(e) {
        try {
            e.preventDefault();
            this.controller.onClick();

            const session = new ApplePaySession(4, this.createPaymentRequest());

            session.oncancel = this.onCancel.bind(this);
            session.onvalidatemerchant = this.onValidateMerchant.bind(this, session);
            session.onpaymentauthorized = this.onPaymentAuthorized.bind(this, session);
            session.onshippingmethodselected = this.onShippingMethodSelected.bind(this, session);
            session.onshippingcontactselected = this.onShippingContactSelected.bind(this, session);
            if (!this.controller.needsShipping() && !this.controller.context.isOrderPay()) {
                session.onpaymentmethodselected = this.onPaymentMethodSelected.bind(this, session);
            }
            session.begin();
        } catch (error) {
            this.controller.submitError(error?.statusMessage || error);
        }
    }

    createPaymentRequest() {
        const context = this.controller.context;
        // Determine if we need to request shipping info from Apple Pay
        // On checkout, only request shipping info if it's the express checkout button.
        const shouldRequestShipping = this.controller.needsShipping() && !context.isCheckout();

        const request = {
            countryCode: this.applepayConfig.countryCode,
            merchantCapabilities: this.applepayConfig.merchantCapabilities,
            supportedNetworks: this.applepayConfig.supportedNetworks,
            currencyCode: this.controller.getCurrency(),
            total: {
                label: this.settings.i18n.total_label,
                type: 'final',
                amount: this.controller.getTotal()
            },
            lineItems: this.mapLineItemsToApplePay(this.controller.getLineItems()),
            requiredShippingContactFields: []
        };

        const needsContactFields = !context.isCheckout();

        if (needsContactFields) {
            request.requiredBillingContactFields = ['postalAddress'];
            if (isFieldRequired('shipping_phone') || isFieldRequired('billing_phone')) {
                request.requiredShippingContactFields.push('phone');
            }
            if (isFieldRequired('shipping_email') || isFieldRequired('billing_email')) {
                request.requiredShippingContactFields.push('email');
            }
        }


        if (shouldRequestShipping) {
            request.requiredShippingContactFields.push('name', 'postalAddress');
            if (this.controller.getShippingOptions().length > 0) {
                request.shippingMethods = this.mapShippingOptionsToApplePay(
                    this.controller.getShippingOptions(),
                    this.controller.getSelectedShippingMethod()
                )
            }
        }

        return this.actions.applyFilters('applepay_payment_request', request, this);
    }

    onCancel() {
        this.controller.onCancel();
    }

    async onValidateMerchant(session, event) {
        try {
            const context = this.controller.context;

            const result = await this.applepay.validateMerchant({
                validationUrl: event.validationURL,
                displayName: this.settings.display_name
            });

            if (context.isCheckout() || context.isOrderPay()) {
                const valid = await this.controller.validate();
                if (!valid) {
                    session.abort();
                }
            } else if (context.isProduct()) {
                // If this is the product page, add to cart.
                this.orderId = await this.controller.addToCart();
            }

            session.completeMerchantValidation(result.merchantSession);
        } catch (error) {
            session.abort();
            this.controller.submitError({
                code: error.errorName,
                message: error.message
            });
        }
    }

    async onPaymentMethodSelected(session, event) {
        try {
            const {billingContact = null} = event.paymentMethod;
            const response = await this.controller.onBillingChange({
                address: this.formatShippingAddress(billingContact)
            });
            session.completePaymentMethodSelection({
                newTotal: {
                    label: this.settings.i18n.total_label,
                    type: 'final',
                    amount: response.total
                },
                newLineItems: this.mapLineItemsToApplePay(response.lineItems)
            });
        } catch (error) {
            session.completePaymentMethodSelection({
                newTotal: {
                    label: this.settings.i18n.total_label,
                    type: 'final',
                    amount: this.controller.getTotal()
                },
                newLineItems: this.mapLineItemsToApplePay(this.controller.getLineItems())
            });
        }
    }

    async onShippingMethodSelected(session, event) {
        const {identifier = null} = event.shippingMethod;
        try {
            const response = await this.controller.onShippingChange({
                address: null,
                shipping_method: identifier
            });

            session.completeShippingMethodSelection({
                newLineItems: this.mapLineItemsToApplePay(response.lineItems),
                newTotal: {
                    label: this.settings.i18n.total_label,
                    type: 'final',
                    amount: response.total
                }
            });
        } catch (error) {
            session.abort();
            this.controller.submitError(error);
        }
    }

    async onShippingContactSelected(session, event) {
        try {
            const {shippingContact} = event;
            const address = this.formatShippingAddress(shippingContact);
            const response = await this.controller.onShippingChange({
                address,
                shipping_method: null
            });
            session.completeShippingContactSelection({
                newLineItems: this.mapLineItemsToApplePay(response.lineItems),
                newShippingMethods: this.mapShippingOptionsToApplePay(response.shippingOptions, response.selectedShippingMethod),
                newTotal: {
                    label: this.settings.i18n.total_label,
                    type: 'final',
                    amount: response.total
                }
            });
        } catch (error) {
            session.completeShippingContactSelection({
                newTotal: {
                    label: this.settings.i18n.total_label,
                    type: 'final',
                    amount: this.controller.getTotal()
                },
                errors: [
                    new ApplePayError('addressUnserviceable')
                ]
            });
        }
    }

    /**
     * @param {ApplePaySession} session
     * @param {ApplePayPaymentAuthorizedEvent} event
     * @return {Promise<void>}
     */
    async onPaymentAuthorized(session, event) {
        try {
            // create the PayPal order
            let orderId;

            if (this.orderId) {
                orderId = this.orderId;
            } else {
                orderId = await this.controller.createOrder();
            }
            // if event.payment.billingContact is empty, use checkout field address
            const billingContact = event.payment.billingContact || this.getBillingContact();

            const response = await this.applepay.confirmOrder({
                orderId,
                token: event.payment.token,
                billingContact
            });

            session.completePayment(ApplePaySession.STATUS_SUCCESS);

            await this.handleOrderStatus(response, orderId);

            const standardizedData = this.extractStandardizedCheckoutData(event);

            this.controller.submit(standardizedData);
        } catch (error) {
            this.controller.submitError(error.message);
            session.completePayment(ApplePaySession.STATUS_FAILURE);
        } finally {
            this.orderId = null;
        }
    }

    async handleOrderStatus(response, orderId) {
        if (response.approveApplePayPayment.status === 'APPROVED') {
            // submit the controller
            setFieldValue(this.getOrderFieldKey(), orderId, null);
        }
    }

    getBillingContact() {
        return {
            givenName: getFieldValue('billing_first_name'),
            familyName: getFieldValue('billing_last_name'),
            locality: getFieldValue('billing_city'),
            addressLines: [
                getFieldValue('billing_address_1'),
                getFieldValue('billing_address_2')
            ].filter(Boolean),
            postalCode: getFieldValue('billing_postcode'),
            administrativeArea: getFieldValue('billing_state'),
            countryCode: getFieldValue('billing_country')
        }
    }

    mapLineItemsToApplePay(items) {
        return items.map(item => {
            return {
                type: 'final',
                label: item.label,
                amount: item.amount
            }
        });
    }

    /**
     * Map generic shipping options to Apple Pay shipping options format
     * @param {Array} shippingOptions - Generic shipping options from controller
     * @param {String} selectedOption
     * @returns {Array} Apple Pay formatted shipping options
     */
    mapShippingOptionsToApplePay(shippingOptions, selectedOption) {
        return shippingOptions
            .map(option => ({
                label: option.label,
                detail: option.description || '',
                amount: option.amount,
                identifier: option.id,
            }))
            .sort((a, b) => {
                // Place selected option first
                if (a.identifier === selectedOption) return -1;
                if (b.identifier === selectedOption) return 1;
                // Sort remaining options by amount (lowest to highest)
                return parseFloat(a.amount) - parseFloat(b.amount);
            });
    }

    extractStandardizedCheckoutData(checkoutData) {
        let data = {
            billing: {},
            shipping: {}
        };
        if (!checkoutData.payment) {
            return data;
        }
        const payment = checkoutData.payment;

        if (payment.billingContact) {
            const address = payment.billingContact;
            const billing = {};
            billing.first_name = address.givenName || '';
            billing.last_name = address.familyName || '';
            billing.address_1 = address.addressLines?.[0] || '';
            billing.address_2 = (address.addressLines?.slice(1).join(' ')) || '';
            billing.city = address.locality || '';
            billing.state = address.administrativeArea || '';
            billing.postcode = address.postalCode || '';
            billing.country = address.countryCode || '';

            if (address.phoneNumber) {
                billing.phone = address.phoneNumber;
            }
            if (address.emailAddress) {
                billing.email = address.emailAddress;
            }

            data.billing = billing;
        }

        if (payment.shippingContact) {
            const address = payment.shippingContact;
            const shipping = {};
            shipping.first_name = address.givenName || '';
            shipping.last_name = address.familyName || '';
            shipping.address_1 = address.addressLines?.[0] || '';
            shipping.address_2 = (address.addressLines?.slice(1).join(' ')) || '';
            shipping.city = address.locality || '';
            shipping.state = address.administrativeArea || '';
            shipping.postcode = address.postalCode || '';
            shipping.country = address.countryCode || '';

            if (address.phoneNumber) {
                shipping.phone = address.phoneNumber;
                data.billing.phone = address.phoneNumber;
            }
            if (address.emailAddress) {
                shipping.email = address.emailAddress;
                data.billing.email = address.emailAddress;
            }

            data.shipping = shipping;
        }
        return data;
    }

    formatShippingAddress(address) {
        return {
            postcode: address.postalCode,
            city: address.locality,
            state: address.administrativeArea?.toUpperCase(),
            country: address.countryCode?.toUpperCase()

        }
    }

    disablePaymentElement() {
        const context = this.controller.context;
        if (context.isOrderPay()) {
            return;
        }
        this.$button?.prop('disabled', true);
        this.$button?.addClass('disabled');
    }

    enablePaymentElement() {
        this.$button?.prop('disabled', false);
    }

    destroyPaymentElement() {
        this.$button?.remove();
    }

}

export default ApplePayGateway;

export {
    ApplePayGateway
}